import React, { useCallback, useState } from "react";
import isEqual from "react-fast-compare";
import TableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import TableRow from "@mui/material/TableRow";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import timeOptions from "../../../constants/timeOptions";
import useMutation from "../../../hooks/useMutation";
import {
  DELETE_INVENTORY_ITEM_MUTATION,
  UPDATE_INVENTORY_ITEM_MUTATION,
  ADD_INVENTORY_ITEM_MUTATION,
} from "../../../constants/graphql/mutations";
import InventoryItemAddress from "../../shopping/Cart/InventoryItemAddress";
import { pricer } from "../../../utils/money";
import { getImage } from "../../shopping/Cart/CartItemFields";
import ItemId from "../../../views/shopping/Orders/ItemId";
import Dimensions from "../../../views/inventory/requests/Dimensions";
import DispositionAutocomplete from "src/views/inventory/requests/disposition/DispositionAutocomplete";
import Input from "../../../components/form/Input";
import Select from "../../../components/form/Select";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import CartItemQuantity from "./CartItemQuantity";
import useQuery from "../../../hooks/useQuery";

const styles = {
  loadingRow: {
    border: 0,
  },
  loadingCell: {
    padding: 0,
    border: 0,
  },
  row: {
    "& > *": {
      borderBottom: "unset",
    },
  },
};

export function getInventoryFields(inventoryItem) {
  return {
    projectId: inventoryItem.projectId,
    assetId: inventoryItem.assetId,
    quantity: inventoryItem.quantity,
    deliveryDate: inventoryItem.deliveryDate,
    deliveryTime: inventoryItem.deliveryTime,
    building: inventoryItem.building || "",
    floor: inventoryItem.floor || "",
    room: inventoryItem.room || "",
    comment: inventoryItem.comment || "",
    location: inventoryItem.location || "",
    street: inventoryItem.street || "",
    city: inventoryItem.city || "",
    zip: inventoryItem.zip || "",
    poNumber: inventoryItem.poNumber || "",
    disposition: inventoryItem.disposition || "",
    idsrNumber: inventoryItem.idsrNumber || "",
    eocNumber: inventoryItem.eocNumber || "",
    pickedStatus: inventoryItem.pickedStatus,
    position: inventoryItem.position,
  };
}

export function getInternalWorkOrderFields(internalWorkOrderItem) {
  return {
    assetId: internalWorkOrderItem.assetId,
    quantity: internalWorkOrderItem.quantity,
    comment: internalWorkOrderItem.comment || "",
    rackLocationChanges: internalWorkOrderItem.rackLocationChanges,
    requestedById: internalWorkOrderItem.requestedById,
    completedStatus: internalWorkOrderItem.completedStatus,
    pickedStatus: internalWorkOrderItem.pickedStatus,
  };
}

function CartItemFields({
  price,
  inventoryItem,
  display,
  submitCounter,
  asset,
  removeInventoryItem,
  setFieldValue,
  inventoryItemsCount,
  disabled = false,
  centerAligned = false,
}) {
  const query = useQuery();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updateItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION);
  const [deleteInventoryItem] = useMutation(DELETE_INVENTORY_ITEM_MUTATION);
  const [addInventoryItem] = useMutation(ADD_INVENTORY_ITEM_MUTATION);
  const photo = getImage(asset);
  const index = inventoryItem.index;
  const inventoryRequestId = query.get("inventoryRequestId");
  const align = centerAligned ? "center" : undefined;
  const centeredCellSx = centerAligned ? { verticalAlign: "middle" } : undefined;
  const isRequiredCompanion = inventoryItem.requiredCompanion;
  const locked = disabled || isRequiredCompanion;

  const handleDelete = useCallback(
    async id => {
      try {
        setDeleteLoading(true);
        await deleteInventoryItem({
          variables: { id },
          onSuccess: async () => {
            removeInventoryItem(id);
            if (inventoryItemsCount === 1) {
              setFieldValue("location", inventoryItem.location);
            }
          },
        });
      } catch (error) {
        console.error("Failed to delete inventory item:", error);
      } finally {
        setDeleteLoading(false);
      }
    },
    [
      deleteInventoryItem,
      removeInventoryItem,
      setFieldValue,
      inventoryItem.location,
      inventoryItemsCount,
    ]
  );

  const handleCreateNewWithQuantity = async newQuantity => {
    setLoading(true);
    try {
      await deleteInventoryItem({
        variables: { id: inventoryItem.id },
      });
      const fields = getInventoryFields(inventoryItem);
      await addInventoryItem({
        variables: {
          ...fields,
          quantity: newQuantity,
          inventoryRequestId,
        },
        onSuccess: data => {
          const newInventoryItem = data.inventoryItem;
          setFieldValue(`inventoryItems[${index}]`, newInventoryItem);
          setLoading(false);
        },
      });
    } catch (error) {
      console.error("Failed to update inventory item:", error);
      setLoading(false);
    }
  };

  const handleChange = values => {
    setLoading(true);
    const variables = {
      id: inventoryItem.id,
      ...getInventoryFields({
        ...inventoryItem,
        projectId: inventoryItem.project.id,
        assetId: asset.id,
        ...values,
      }),
    };
    return updateItem({
      variables,
      onSuccess: () => {
        Object.entries(values).forEach(([key, value]) => {
          setFieldValue(`inventoryItems[${index}].${key}`, value);
        });
        setLoading(false);
      },
      onFailure: () => {
        setLoading(false);
      },
    });
  };

  return (
    <React.Fragment key={`cartItem${asset.id}`}>
      {loading && (
        <TableRow sx={styles.loadingRow}>
          <TableCell colSpan={1000} sx={styles.loadingCell}>
            <Box mt="-3px">
              <LinearProgress sx={{ height: "2px" }} />
            </Box>
          </TableCell>
        </TableRow>
      )}
      <TableRow sx={styles.row} id={`cartItem-${asset.id}`}>
        {display.ii_image && (
          <TableCell align={align} sx={centeredCellSx}>
            {photo ? <img style={{ maxWidth: 90 }} src={photo} /> : "No image"}
          </TableCell>
        )}
        {display.ii_item_id && (
          <TableCell align={align} sx={centeredCellSx}>
            <Box>
              <ItemId asset={asset} />
              {isRequiredCompanion && (
                <Typography variant="caption" color="text.secondary">
                  Required companion
                </Typography>
              )}
            </Box>
          </TableCell>
        )}
        {display.ii_sku_number && (
          <TableCell align={align} sx={centeredCellSx}>{asset.skuNumber}</TableCell>
        )}
        {display.ii_barcode && <TableCell align={align} sx={centeredCellSx}>{asset.barcode}</TableCell>}
        {display.ii_description && (
          <TableCell align={align} sx={centeredCellSx}>{asset.description}</TableCell>
        )}
        {display.ii_model && <TableCell align={align} sx={centeredCellSx}>{asset.model}</TableCell>}
        {display.ii_dimensions && (
          <TableCell align={align} sx={centeredCellSx}>
            <Dimensions dimensions={asset.dimensions} />
          </TableCell>
        )}
        {display.ii_qtyRequested && (
          <TableCell align={align} sx={centeredCellSx}>
            <CartItemQuantity
              disabled={locked || asset.type === "Kit" || inventoryItem.pickedStatus}
              value={inventoryItem.quantity || ""}
              availableQuantity={asset.availableQuantity}
              centerValue={centerAligned}
              onChange={quantity => handleCreateNewWithQuantity(quantity)}
            />
          </TableCell>
        )}
        {display.ii_building && (
          <TableCell align={align} sx={centeredCellSx}>
            <Input
              label="Building"
              name={`inventoryItems[${index}].building`}
              disabled={locked}
            />
          </TableCell>
        )}
        {display.ii_floor && (
          <TableCell align={align} sx={centeredCellSx}>
            <Input label="Floor" name={`inventoryItems[${index}].floor`} disabled={locked} />
          </TableCell>
        )}
        {display.ii_room && (
          <TableCell align={align} sx={centeredCellSx}>
            <Input label="Room" name={`inventoryItems[${index}].room`} disabled={locked} />
          </TableCell>
        )}
        {display.ii_po_number && (
          <TableCell align={align} sx={centeredCellSx}>
            <Input
              label="PO Number"
              name={`inventoryItems[${index}].poNumber`}
              disabled={locked}
            />
          </TableCell>
        )}
        {display.ii_idsr_number && (
          <TableCell align={align} sx={centeredCellSx}>
            <Input
              label="IDSR/Proj. Name"
              name={`inventoryItems[${index}].idsrNumber`}
              disabled={locked}
            />
          </TableCell>
        )}
        {display.ii_eoc_number && (
          <TableCell align={align} sx={centeredCellSx}>
            <Input label="EOC" name={`inventoryItems[${index}].eocNumber`} disabled={locked} />
          </TableCell>
        )}
        {display.ii_deliveryDate && (
          <TableCell align={align} sx={centeredCellSx}>
            <Input type="date" name={`inventoryItems[${index}].deliveryDate`} disabled={locked} />
          </TableCell>
        )}
        {display.ii_deliveryTime && (
          <TableCell align={align} sx={centeredCellSx}>
            <Select
              size="small"
              name={`inventoryItems[${index}].deliveryTime`}
              options={timeOptions.map(v => [v, v])}
              disabled={locked}
            />
          </TableCell>
        )}
        {display.sc_price && (
          <TableCell align={align} sx={centeredCellSx}>
            {pricer(price ?? asset?.unitCost)}
          </TableCell>
        )}
        <TableCell align={align} sx={centeredCellSx}>
          {!locked && (
            <Tooltip arrow title="Remove Item">
              <IconButton disabled={deleteLoading} onClick={() => setShowDeleteModal(true)}>
                {deleteLoading ? <CircularProgress size={24} /> : <DeleteIcon />}
              </IconButton>
            </Tooltip>
          )}
          {showDeleteModal && (
            <ConfirmDeleteModal
              title="Remove Item"
              applyLabel="Remove"
              descr="Are you sure you want to remove the selected item from your order?"
              onClose={() => setShowDeleteModal(false)}
              onDelete={() => handleDelete(inventoryItem.id)}
            />
          )}
        </TableCell>
      </TableRow>
      {display.ii_location && (
        <TableRow sx={styles.row}>
          <TableCell colSpan="22">
            <Box mt={-2}>
              <InventoryItemAddress
                error={!inventoryItem.location && submitCounter > 0}
                hideDropdown={inventoryItem.street || inventoryItem.city || inventoryItem.zip}
                inventoryItem={inventoryItem}
                disabled={locked}
                onChange={values => {
                  Object.entries(values).forEach(([key, value]) => {
                    setFieldValue(`inventoryItems[${index}].${key}`, value);
                  });
                }}
              />
            </Box>
          </TableCell>
        </TableRow>
      )}
      {display.ii_disposition && (
        <TableRow>
          <TableCell colSpan="22">
            <Box mt={-2}>
              <DispositionAutocomplete
                value={inventoryItem.disposition || ""}
                disabled={locked}
                onChange={value => {
                  setFieldValue(`inventoryItems[${index}].disposition`, value);
                }}
              />
            </Box>
          </TableCell>
        </TableRow>
      )}
      {display.ii_notes && (
        <TableRow>
          <TableCell colSpan="22">
            <Box mt={-2}>
              <Input
                multiline
                rows={2}
                label="Notes"
                name={`inventoryItems[${index}].comment`}
                disabled={locked}
              />
            </Box>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

export default React.memo(CartItemFields, isEqual);
